// Scouting report as a PNG for Discord: drawn on a canvas in the browser, no server and no library involved.
// Champion icons come from our own /dd proxy (same origin) in production, so the canvas stays exportable.
import type { DraftRec, ScoutResult } from "../types";
import { getChampionIconUrl, getChampionName } from "./ddragon";

const W = 1200;
const PAD = 48;
const GAP = 16;
const ROW = 76;
const ICON = 56;
// the site's palette (index.css)
const C = { page: "#0c0f14", surface: "#141922", raised: "#1b212c", line: "#262e3b", ink: "#eef1f5", ink2: "#b3bccb", ink3: "#8591a5", signal: "#c6f432", onSignal: "#0c0f14" };
const font = (size: number, weight = 600) => `${weight} ${size}px Archivo, ui-sans-serif, system-ui, sans-serif`;
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

/** Icon or null: a missing icon must not sink the whole image. */
async function loadIcon(championId: number): Promise<HTMLImageElement | null> {
	const img = new Image();
	img.crossOrigin = "anonymous"; // local dev loads icons from Riot's CDN: without CORS mode they would taint the canvas
	img.src = getChampionIconUrl(championId);
	try {
		await img.decode();
		return img;
	} catch {
		return null;
	}
}

/** Shortens `text` with an ellipsis until it fits `max` pixels in the current font. */
function fit(ctx: CanvasRenderingContext2D, text: string, max: number): string {
	if (ctx.measureText(text).width <= max) return text;
	let t = text;
	while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
	return `${t.trimEnd()}…`;
}

/** Word-wraps `text` to `max` pixels, at most `maxLines` lines (the last one gets an ellipsis). */
function wrap(ctx: CanvasRenderingContext2D, text: string, max: number, maxLines: number): string[] {
	const lines: string[] = [];
	let line = "";
	for (const word of text.split(/\s+/)) {
		const next = line ? `${line} ${word}` : word;
		if (ctx.measureText(next).width <= max || !line) line = next;
		else {
			lines.push(line);
			line = word;
		}
	}
	if (line) lines.push(line);
	if (lines.length <= maxLines) return lines;
	return [...lines.slice(0, maxLines - 1), fit(ctx, `${lines[maxLines - 1]} ${lines[maxLines] ?? ""}`, max)];
}

export async function renderScoutImage(data: ScoutResult): Promise<Blob> {
	const report = data.report;
	if (!report) throw new Error("No report to draw");
	await document.fonts?.ready;

	type Step = { title: string; hint: string; recs: DraftRec[]; ban: boolean };
	const steps: Step[] = [
		{ title: "BAN PHASE 1", hint: "our bans", recs: report.bans1, ban: true },
		{ title: "PICK PHASE 1", hint: "expect from them", recs: report.picks1, ban: false },
		{ title: "BAN PHASE 2", hint: "our bans", recs: report.bans2, ban: true },
		{ title: "PICK PHASE 2", hint: "expect from them", recs: report.picks2, ban: false },
	];
	// our side of the draft (only with our own roster): one full-width strip each, champions side by side
	const first = report.firstPick ? `first pick on blue: ${getChampionName(report.firstPick.championId)}` : "in lane order";
	const strips: Step[] = [
		{ title: "THEIR LIKELY BANS", hint: "expect against us", recs: report.theirBans ?? [], ban: true },
		{ title: "OUR PICKS", hint: first, recs: report.ourPicks ?? [], ban: false },
	].filter((s) => s.recs.length);
	const icons = new Map<number, HTMLImageElement | null>();
	await Promise.all([...steps, ...strips].flatMap((s) => s.recs).map(async (r) => icons.set(r.championId, await loadIcon(r.championId))));

	// Drawn on a tall canvas, then cropped to the height actually used.
	const canvas = document.createElement("canvas");
	canvas.width = W;
	canvas.height = 1800;
	const ctx = canvas.getContext("2d")!;
	ctx.textBaseline = "top";
	ctx.fillStyle = C.page;
	ctx.fillRect(0, 0, W, canvas.height);
	const text = (s: string, x: number, y: number, color: string, f: string, align: CanvasTextAlign = "left") => {
		ctx.font = f;
		ctx.fillStyle = color;
		ctx.textAlign = align;
		ctx.fillText(s, x, y);
	};

	// header
	let y = PAD;
	text("RIFTWATCH  ·  SCOUTING REPORT", PAD, y, C.signal, font(15, 800));
	text(new Date(data.generatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), W - PAD, y, C.ink3, font(15), "right");
	y += 34;
	ctx.font = font(40, 800);
	text(fit(ctx, data.players.map((p) => p.riotId.split("#")[0]).join("  ·  "), W - 2 * PAD), PAD, y, C.ink, font(40, 800));
	y += 56;
	const t = data.team;
	const summary = t.games
		? `${t.wins}W ${t.games - t.wins}L in ${t.games} team games (${t.tournamentGames} tournament)  ·  Blue side ${pct(t.blue.wins, t.blue.games)}%  ·  Red side ${pct(t.red.wins, t.red.games)}%  ·  First dragon ${t.first.dragon}%`
		: "No team games found: based on each player's own ranked games";
	ctx.font = font(18);
	text(fit(ctx, summary, W - 2 * PAD), PAD, y, C.ink2, font(18));
	y += 44;

	const card = (s: Step, x: number, w: number, h: number) => {
		ctx.fillStyle = C.surface;
		ctx.beginPath();
		ctx.roundRect(x, y, w, h, 12);
		ctx.fill();
		ctx.strokeStyle = C.line;
		ctx.stroke();
		text(s.title, x + 16, y + 16, s.ban ? C.signal : C.ink, font(14, 800));
		text(s.hint, x + w - 16, y + 17, C.ink3, font(13), "right");
	};
	/** icon, champion name and whose champion it is, in a cell `w` wide */
	const rec = (r: DraftRec, x: number, ry: number, w: number, ban: boolean) => {
		const img = icons.get(r.championId);
		ctx.save();
		ctx.beginPath();
		ctx.roundRect(x, ry, ICON, ICON, 10);
		ctx.clip();
		if (img) ctx.drawImage(img, x, ry, ICON, ICON);
		else {
			ctx.fillStyle = C.raised;
			ctx.fillRect(x, ry, ICON, ICON);
		}
		if (ban) {
			// a ban reads as "struck out" even without colour
			ctx.fillStyle = "rgba(12,15,20,.45)";
			ctx.fillRect(x, ry, ICON, ICON);
			ctx.strokeStyle = C.ink;
			ctx.lineWidth = 3;
			ctx.beginPath();
			ctx.moveTo(x, ry + ICON);
			ctx.lineTo(x + ICON, ry);
			ctx.stroke();
			ctx.lineWidth = 1;
		}
		ctx.restore();
		const tx = x + ICON + 12;
		ctx.font = font(19, 800);
		text(fit(ctx, getChampionName(r.championId), w - ICON - 12), tx, ry + 7, C.ink, font(19, 800));
		ctx.font = font(14);
		text(fit(ctx, r.player ? r.player.split("#")[0]! : "team-wide", w - ICON - 12), tx, ry + 33, C.ink3, font(14));
	};

	// draft plan: four columns in tournament-draft order
	const colW = (W - 2 * PAD - 3 * GAP) / 4;
	const rows = Math.max(1, ...steps.map((s) => s.recs.length));
	const cardH = 52 + rows * ROW + 4;
	steps.forEach((s, i) => {
		const x = PAD + i * (colW + GAP);
		card(s, x, colW, cardH);
		s.recs.forEach((r, j) => rec(r, x + 16, y + 52 + j * ROW, colW - 32, s.ban));
	});
	y += cardH + GAP;
	for (const s of strips) {
		card(s, PAD, W - 2 * PAD, 52 + ROW);
		const cell = (W - 2 * PAD - 32) / 5;
		s.recs.slice(0, 5).forEach((r, j) => rec(r, PAD + 16 + j * cell, y + 52, cell - 12, s.ban));
		y += 52 + ROW + GAP;
	}
	y += 32 - GAP;

	// game plan
	if (report.gamePlan.length) {
		text("GAME PLAN", PAD, y, C.signal, font(14, 800));
		y += 28;
		ctx.font = font(18, 500);
		for (const line of report.gamePlan.slice(0, 3)) {
			ctx.fillStyle = C.ink3;
			ctx.beginPath();
			ctx.arc(PAD + 4, y + 11, 3, 0, Math.PI * 2);
			ctx.fill();
			for (const l of wrap(ctx, line, W - 2 * PAD - 20, 2)) {
				text(l, PAD + 20, y, C.ink2, font(18, 500));
				y += 26;
			}
			y += 8;
		}
		y += 12;
	}

	// footer
	ctx.fillStyle = C.line;
	ctx.fillRect(PAD, y, W - 2 * PAD, 1);
	y += 18;
	if (data.id) text(`${location.host}/scout/${data.id}`, PAD, y, C.ink3, font(15));
	text("AI-written from recent games · verify before you draft", W - PAD, y, C.ink3, font(15), "right");
	y += 20 + PAD - 12;

	const out = document.createElement("canvas");
	out.width = W;
	out.height = y;
	out.getContext("2d")!.drawImage(canvas, 0, 0);
	return new Promise((resolve, reject) => out.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not create the image"))), "image/png"));
}
